<script setup>
import { computed, onMounted, onUnmounted, ref } from 'vue'
import { useNeoStore } from '../../../../../state/neo.js'

const neoStore = useNeoStore()
onMounted(() => { neoStore.panelWidth = 260 })
onUnmounted(() => { neoStore.panelWidth = 0 })

const props = defineProps({
    allTags: { type: Array, default: () => [] },
    tagCounts: { type: Object, default: () => ({}) },
    reservationForms: { type: Array, default: () => [] },
    filters: { type: Object, default: () => ({}) },
    filteredTotal: { type: Number, default: 0 },
})

const emit = defineEmits([
    'update:search',
    'toggle-tag',
    'toggle-form',
    'update:date-from',
    'update:date-to',
    'update:activity-from',
    'update:activity-to',
    'reset',
    'export',
])

// ─── Barvy tagů ───────────────────────────────────────────────────

const TAG_COLORS = [
    '#6366f1', '#ec4899', '#f59e0b', '#10b981',
    '#3b82f6', '#ef4444', '#8b5cf6', '#14b8a6',
    '#f97316', '#84cc16',
]

function getTagColor(index) {
    return TAG_COLORS[index % TAG_COLORS.length]
}

function isTagActive(tag) {
    return (props.filters.tags ?? []).includes(tag)
}

function isFormActive(id) {
    return (props.filters.form_ids ?? []).map(Number).includes(id)
}

// ─── "Zobrazit více" ──────────────────────────────────────────────

const MAX_VISIBLE = 5
const showAllTags = ref(false)
const showAllForms = ref(false)

const visibleTags = computed(() =>
    showAllTags.value ? props.allTags : props.allTags.slice(0, MAX_VISIBLE)
)
const hiddenTagsCount = computed(() => Math.max(0, props.allTags.length - MAX_VISIBLE))

const visibleForms = computed(() =>
    showAllForms.value ? props.reservationForms : props.reservationForms.slice(0, MAX_VISIBLE)
)
const hiddenFormsCount = computed(() => Math.max(0, props.reservationForms.length - MAX_VISIBLE))

// ─── Export ───────────────────────────────────────────────────────

const exportOpen = ref(false)

const hasActiveFilters = computed(() =>
    !!(props.filters.tags?.length || props.filters.form_ids?.length ||
       props.filters.date_from || props.filters.date_to ||
       props.filters.activity_from || props.filters.activity_to)
)
</script>

<template>
    <div
        class="w-[260px] flex-shrink-0 bg-white dark:bg-neutral-900 border-r border-neutral-100 dark:border-neutral-800 flex flex-col h-dvh overflow-y-auto max-lg:hidden"
    >
        <div class="flex flex-col gap-3 px-3.5 pt-3.5">

            <!-- Search -->
            <div class="flex items-center gap-2 px-3 py-2 rounded-xl bg-neutral-50 dark:bg-neutral-800 text-neutral-400 text-sm">
                <i class="pi pi-search !text-sm flex-shrink-0" />
                <input
                    :value="filters.search ?? ''"
                    :placeholder="$t('contacts.search_placeholder_client')"
                    class="bg-transparent border-0 outline-none flex-1 text-neutral-800 dark:text-neutral-100 placeholder:text-neutral-400 dark:placeholder:text-neutral-500 min-w-0 text-sm"
                    @input="emit('update:search', $event.target.value)"
                />
                <button
                    v-if="filters.search"
                    class="flex-shrink-0 text-neutral-400 hover:text-neutral-600 dark:hover:text-neutral-200 transition-colors"
                    @click="emit('update:search', '')"
                >
                    <i class="pi pi-times !text-xs" />
                </button>
            </div>

            <!-- Export dropdown -->
            <div class="relative">
                <button
                    class="flex items-center gap-2 w-full px-4 py-2 rounded-xl bg-neutral-100 dark:bg-neutral-800 text-neutral-700 dark:text-neutral-200 font-semibold text-sm hover:bg-neutral-200 dark:hover:bg-neutral-700 transition-colors"
                    @click="exportOpen = !exportOpen"
                >
                    <i class="pi pi-download !text-xs" />
                    {{ $t('contacts.btn_export') }}
                    <i class="pi pi-chevron-down !text-[10px] ml-auto opacity-60" />
                </button>
                <div
                    v-if="exportOpen"
                    class="absolute top-full mt-1 left-0 right-0 z-50 bg-white dark:bg-neutral-800 border border-neutral-200 dark:border-neutral-700 rounded-xl shadow-lg overflow-hidden"
                >
                    <button
                        class="flex items-center gap-2 w-full px-4 py-2.5 text-sm text-neutral-700 dark:text-neutral-200 hover:bg-neutral-50 dark:hover:bg-neutral-700 transition-colors"
                        @click="emit('export', 'csv'); exportOpen = false"
                    >
                        <i class="pi pi-file !text-sm text-green-600" />{{ $t('contacts.export_csv') }}
                    </button>
                    <button
                        class="flex items-center gap-2 w-full px-4 py-2.5 text-sm text-neutral-700 dark:text-neutral-200 hover:bg-neutral-50 dark:hover:bg-neutral-700 transition-colors"
                        @click="emit('export', 'xlsx'); exportOpen = false"
                    >
                        <i class="pi pi-file-excel !text-sm text-green-700" />{{ $t('contacts.export_xlsx') }}
                    </button>
                </div>
            </div>

            <!-- Tagy -->
            <div v-if="allTags.length" class="flex flex-col gap-0">
                <div class="flex items-center justify-between pb-1 px-1">
                    <div class="flex items-center gap-2">
                        <span class="text-[11px] font-semibold text-neutral-500 dark:text-neutral-400 uppercase tracking-wider">{{ $t('contacts.filter_tags') }}</span>
                        <span class="bg-neutral-100 dark:bg-neutral-700 text-neutral-500 dark:text-neutral-300 rounded px-1.5 py-px text-[10px] font-semibold">
                            {{ allTags.length }}
                        </span>
                    </div>
                    <button
                        v-if="hasActiveFilters"
                        class="text-[11px] text-red-400 font-medium hover:text-red-600 transition-colors"
                        @click="emit('reset')"
                    >
                        {{ $t('contacts.filter_reset') }}
                    </button>
                </div>

                <div
                    v-for="(tag, i) in visibleTags"
                    :key="tag"
                    class="flex items-center gap-2 px-1.5 py-1.5 rounded-lg cursor-default hover:bg-neutral-50 dark:hover:bg-neutral-800 transition-colors"
                    :class="isTagActive(tag) ? '' : 'opacity-40'"
                    @click="emit('toggle-tag', tag)"
                >
                    <i
                        class="!text-[12px] flex-shrink-0 w-4 transition-colors"
                        :class="isTagActive(tag) ? 'pi pi-eye' : 'pi pi-eye-slash text-neutral-300'"
                        :style="isTagActive(tag) ? { color: getTagColor(i) } : {}"
                    />
                    <div class="w-2 h-2 rounded-sm flex-shrink-0" :style="{ background: getTagColor(i) }" />
                    <div class="flex-1 text-[12.5px] text-neutral-800 dark:text-neutral-200 font-medium truncate min-w-0">
                        {{ tag }}
                    </div>
                    <span v-if="tagCounts[tag]" class="text-[11px] text-neutral-400 dark:text-neutral-500 tabular-nums flex-shrink-0">
                        {{ tagCounts[tag] }}
                    </span>
                </div>

                <div class="flex items-center gap-1 mt-0.5">
                    <button
                        v-if="hiddenTagsCount > 0"
                        class="flex items-center gap-1.5 px-1.5 py-1.5 text-[11.5px] text-neutral-500 dark:text-neutral-400 font-medium hover:text-neutral-800 dark:hover:text-neutral-200 hover:bg-neutral-50 dark:hover:bg-neutral-800 rounded-lg transition-colors flex-1"
                        @click="showAllTags = !showAllTags"
                    >
                        <i class="pi pi-tags !text-[10px]" />
                        {{ showAllTags ? $t('contacts.filter_show_less') : $t('contacts.filter_show_all') }}
                        <span
                            v-if="!showAllTags"
                            class="ml-auto bg-neutral-100 dark:bg-neutral-700 text-neutral-500 dark:text-neutral-300 rounded px-1.5 py-px text-[10px] font-semibold"
                        >+{{ hiddenTagsCount }}</span>
                    </button>
                </div>
            </div>

            <!-- Formuláře -->
            <div v-if="reservationForms.length" class="flex flex-col gap-0 pb-1">
                <div class="flex items-center justify-between pb-1 px-1">
                    <span class="text-[11px] font-semibold text-neutral-500 dark:text-neutral-400 uppercase tracking-wider">{{ $t('contacts.filter_forms') }}</span>
                </div>

                <div
                    v-for="form in visibleForms"
                    :key="form.id"
                    class="flex items-center gap-2 px-1.5 py-1.5 rounded-lg cursor-default hover:bg-neutral-50 dark:hover:bg-neutral-800 transition-colors"
                    :class="isFormActive(form.id) ? '' : 'opacity-40'"
                    @click="emit('toggle-form', form.id)"
                >
                    <i
                        class="!text-[12px] flex-shrink-0 w-4 transition-colors"
                        :class="isFormActive(form.id) ? 'pi pi-eye text-blue-500' : 'pi pi-eye-slash text-neutral-300'"
                    />
                    <i class="pi pi-file-o !text-[11px] text-neutral-400 flex-shrink-0" />
                    <div class="flex-1 text-[12.5px] text-neutral-800 dark:text-neutral-200 font-medium truncate min-w-0">
                        {{ form.name }}
                    </div>
                </div>

                <div class="flex items-center gap-1 mt-0.5">
                    <button
                        v-if="hiddenFormsCount > 0"
                        class="flex items-center gap-1.5 px-1.5 py-1.5 text-[11.5px] text-neutral-500 dark:text-neutral-400 font-medium hover:text-neutral-800 dark:hover:text-neutral-200 hover:bg-neutral-50 dark:hover:bg-neutral-800 rounded-lg transition-colors flex-1"
                        @click="showAllForms = !showAllForms"
                    >
                        <i class="pi pi-th-large !text-[10px]" />
                        {{ showAllForms ? $t('contacts.filter_show_less') : $t('contacts.filter_show_all') }}
                        <span
                            v-if="!showAllForms"
                            class="ml-auto bg-neutral-100 dark:bg-neutral-700 text-neutral-500 dark:text-neutral-300 rounded px-1.5 py-px text-[10px] font-semibold"
                        >+{{ hiddenFormsCount }}</span>
                    </button>
                </div>
            </div>

            <!-- Datum registrace -->
            <div class="flex flex-col gap-0">
                <div class="flex items-center pb-1 px-1">
                    <span class="text-[11px] font-semibold text-neutral-500 dark:text-neutral-400 uppercase tracking-wider">{{ $t('contacts.filter_registration') }}</span>
                </div>
                <div class="flex items-center gap-1.5 px-1">
                    <input type="date" :value="filters.date_from ?? ''"
                        class="flex-1 min-w-0 text-[11.5px] bg-neutral-50 dark:bg-neutral-800 border border-neutral-200 dark:border-neutral-700 rounded-lg px-2 py-1.5 text-neutral-700 dark:text-neutral-200 outline-none focus:ring-1 focus:ring-blue-500"
                        @change="emit('update:date-from', $event.target.value)" />
                    <span class="text-[10px] text-neutral-300 dark:text-neutral-600 flex-shrink-0">—</span>
                    <input type="date" :value="filters.date_to ?? ''"
                        class="flex-1 min-w-0 text-[11.5px] bg-neutral-50 dark:bg-neutral-800 border border-neutral-200 dark:border-neutral-700 rounded-lg px-2 py-1.5 text-neutral-700 dark:text-neutral-200 outline-none focus:ring-1 focus:ring-blue-500"
                        @change="emit('update:date-to', $event.target.value)" />
                </div>
            </div>

            <!-- Poslední aktivita -->
            <div class="flex flex-col gap-0 pb-4">
                <div class="flex items-center pb-1 px-1">
                    <span class="text-[11px] font-semibold text-neutral-500 dark:text-neutral-400 uppercase tracking-wider">{{ $t('contacts.filter_last_activity') }}</span>
                </div>
                <div class="flex items-center gap-1.5 px-1">
                    <input type="date" :value="filters.activity_from ?? ''"
                        class="flex-1 min-w-0 text-[11.5px] bg-neutral-50 dark:bg-neutral-800 border border-neutral-200 dark:border-neutral-700 rounded-lg px-2 py-1.5 text-neutral-700 dark:text-neutral-200 outline-none focus:ring-1 focus:ring-blue-500"
                        @change="emit('update:activity-from', $event.target.value)" />
                    <span class="text-[10px] text-neutral-300 dark:text-neutral-600 flex-shrink-0">—</span>
                    <input type="date" :value="filters.activity_to ?? ''"
                        class="flex-1 min-w-0 text-[11.5px] bg-neutral-50 dark:bg-neutral-800 border border-neutral-200 dark:border-neutral-700 rounded-lg px-2 py-1.5 text-neutral-700 dark:text-neutral-200 outline-none focus:ring-1 focus:ring-blue-500"
                        @change="emit('update:activity-to', $event.target.value)" />
                </div>
            </div>

            <!-- Počítadlo -->
            <div class="px-1 pb-4 text-[11px] text-neutral-400 dark:text-neutral-500">
                {{ $t('contacts.contacts_count', { count: filteredTotal }) }}
            </div>

        </div>
    </div>
</template>
